import { useQuery } from '@tanstack/react-query';
import { Briefcase } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { DonutChart } from '@/components/common/DonutChart';
import { LineChart } from '@/components/common/LineChart';
import { getStatistics, type Statistics } from '@/api/statistics.api';

// ─── Hook ───────────────────────────────────────────────────────────────────

function useStatistics() {
  return useQuery<Statistics>({
    queryKey: ['statistics'],
    queryFn: getStatistics,
  });
}

// ─── Hilfskomponenten ───────────────────────────────────────────────────────

function Card({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function Bar({ label, value, max, color = 'bg-blue-500' }: { label: string; value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-16 shrink-0 truncate text-slate-600 sm:w-24" title={label}>{label}</span>
      <div className="flex-1 rounded-full bg-slate-100 h-4">
        <div className={`h-4 rounded-full ${color}`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
      <span className="w-8 text-right font-medium text-slate-700">{value}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-slate-800">{value ?? '—'}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

// ─── Sektionen ──────────────────────────────────────────────────────────────

function OverviewSection({ s }: { s: Statistics }) {
  return (
    <Card title="Übersicht">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Personen" value={s.overview.totalPersons} />
        <Stat label="Beziehungen" value={s.overview.totalFamilies} />
        <Stat label="Generationen" value={s.overview.totalGenerations} />
        <Stat label="Durchschnittsalter" value={s.averageAge ? `${s.averageAge} J.` : null} />
        <Stat label="Älteste Person" value={s.overview.oldestPerson ? `${s.overview.oldestPerson.age} J.` : null} />
      </div>
      {s.overview.oldestPerson && (
        <p className="mt-3 text-center text-xs text-slate-400">
          Älteste Person: {s.overview.oldestPerson.name} ({s.overview.oldestPerson.age} Jahre)
          {s.overview.youngestBirth && <> · Jüngste Geburt: {s.overview.youngestBirth.name} ({s.overview.youngestBirth.year})</>}
        </p>
      )}
    </Card>
  );
}

function GenderSection({ s }: { s: Statistics }) {
  const total = s.genderDistribution.reduce((sum, g) => sum + g.count, 0);
  const colors: Record<string, string> = {
    'Männlich': 'bg-blue-500',
    'Weiblich': 'bg-pink-500',
    'Divers': 'bg-purple-500',
    'Unbekannt': 'bg-slate-400',
  };
  return (
    <Card title="Geschlechterverteilung">
      {/* Pie-like bar */}
      <div className="mb-3 flex h-6 overflow-hidden rounded-full">
        {s.genderDistribution.map((g) => (
          <div
            key={g.gender}
            className={`${colors[g.gender] ?? 'bg-slate-400'}`}
            style={{ width: `${(g.count / total) * 100}%` }}
            title={`${g.gender}: ${g.count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-4 text-sm">
        {s.genderDistribution.map((g) => (
          <div key={g.gender} className="flex items-center gap-1.5">
            <span className={`h-3 w-3 rounded-full ${colors[g.gender] ?? 'bg-slate-400'}`} />
            {g.gender}: {g.count} ({Math.round((g.count / total) * 100)}%)
          </div>
        ))}
      </div>
    </Card>
  );
}

function AgeSection({ s }: { s: Statistics }) {
  const max = Math.max(...s.ageDistribution.map((a) => a.count), 1);
  return (
    <Card title="Altersverteilung (bei Tod)">
      <div className="space-y-1.5">
        {s.ageDistribution.map((a) => (
          <Bar key={a.bracket} label={a.bracket} value={a.count} max={max} color="bg-emerald-500" />
        ))}
      </div>
    </Card>
  );
}

function PlacesSection({ s }: { s: Statistics }) {
  const maxB = Math.max(...s.topBirthPlaces.map((p) => p.count), 1);
  const maxD = Math.max(...s.topDeathPlaces.map((p) => p.count), 1);
  return (
    <>
      <Card title="Geburtsorte">
        {s.birthPlacePie.length === 0 ? (
          <p className="text-sm text-slate-400">Keine Geburtsorte hinterlegt.</p>
        ) : (
          <DonutChart data={s.birthPlacePie} centerLabel="Personen" />
        )}
      </Card>
      <Card title="Herkunftsländer">
        {s.birthCountryPie.length === 0 ? (
          <p className="text-sm text-slate-400">
            Keine Länderangaben in den Geburtsorten gefunden. Tipp: Orte als
            „Stadt, Land" erfassen, damit das Land abgeleitet werden kann.
          </p>
        ) : (
          <>
            <DonutChart data={s.birthCountryPie} centerLabel="Personen" />
            <p className="mt-3 text-xs text-slate-400">Aus dem Ortstext abgeleitet.</p>
          </>
        )}
      </Card>
      <Card title="Häufigste Geburtsorte">
        {s.topBirthPlaces.length === 0 ? (
          <p className="text-sm text-slate-400">Keine Geburtsorte hinterlegt.</p>
        ) : (
          <div className="space-y-1.5">
            {s.topBirthPlaces.map((p) => (
              <Bar key={p.place} label={p.place} value={p.count} max={maxB} color="bg-green-500" />
            ))}
          </div>
        )}
      </Card>
      <Card title="Häufigste Sterbeorte">
        {s.topDeathPlaces.length === 0 ? (
          <p className="text-sm text-slate-400">Keine Sterbeorte hinterlegt.</p>
        ) : (
          <div className="space-y-1.5">
            {s.topDeathPlaces.map((p) => (
              <Bar key={p.place} label={p.place} value={p.count} max={maxD} color="bg-red-400" />
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

function NamesSection({ s }: { s: Statistics }) {
  const max = Math.max(...s.topLastNames.map((n) => n.count), 1);
  return (
    <Card title="Häufigste Nachnamen">
      <div className="space-y-1.5">
        {s.topLastNames.map((n) => (
          <Bar key={n.name} label={n.name} value={n.count} max={max} color="bg-indigo-500" />
        ))}
      </div>
    </Card>
  );
}

// Gender-Farben konsistent mit dem Rest der App (blue/pink/green als Hex,
// damit sie exakt den Serienfarben der Personen-Ansichten entsprechen).
const GENDER_SERIES = [
  { key: 'male', label: 'Männlich', color: '#3b82f6' },
  { key: 'female', label: 'Weiblich', color: '#ec4899' },
  { key: 'other', label: 'Divers/Unbekannt', color: '#22c55e' },
] as const;

function GenderStackRow({
  row,
  maxTotal,
}: {
  row: Statistics['occupationStats']['byGender'][number];
  maxTotal: number;
}) {
  // Segmente mit 0 auslassen, damit keine leeren Lücken entstehen.
  const segments = GENDER_SERIES
    .map((serie) => ({ ...serie, count: row[serie.key] }))
    .filter((seg) => seg.count > 0);
  const total = row.male + row.female + row.other;

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-16 shrink-0 truncate text-slate-600 sm:w-24" title={row.title}>{row.title}</span>
      <div className="flex-1">
        {/* Gestapelter Balken: Breite relativ zum größten Beruf, 2px weiße Lücke zwischen Segmenten */}
        <div className="flex h-4 gap-[2px]" style={{ width: `${Math.max((total / maxTotal) * 100, 3)}%` }}>
          {segments.map((seg, i) => {
            // Anteil an der vollen Spurbreite → reicht der Platz für die Zahl im Segment?
            const pct = (seg.count / maxTotal) * 100;
            return (
              <div
                key={seg.key}
                className={`flex h-4 items-center justify-center overflow-hidden ${i === 0 ? 'rounded-l-full' : ''} ${i === segments.length - 1 ? 'rounded-r-full' : ''}`}
                style={{ backgroundColor: seg.color, flexGrow: seg.count, flexBasis: 0 }}
                title={`${seg.label}: ${seg.count}`}
              >
                {pct >= 8 && (
                  <span className="text-[10px] font-semibold leading-none text-slate-900">{seg.count}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <span className="w-8 shrink-0 text-right font-medium text-slate-700">{total}</span>
    </div>
  );
}

function OccupationSection({ s }: { s: Statistics }) {
  const o = s.occupationStats;
  const icon = <Briefcase className="h-4 w-4" aria-hidden />;

  if (o.personsWithOccupation === 0) {
    return (
      <Card title="Berufe" icon={icon}>
        <p className="text-sm text-slate-400">
          Noch keine Berufsdaten erfasst — Stationen lassen sich im Personen-Panel pflegen.
        </p>
      </Card>
    );
  }

  const maxTop = Math.max(...o.topOccupations.map((t) => t.count), 1);
  const maxGenderTotal = Math.max(...o.byGender.map((g) => g.male + g.female + g.other), 1);

  return (
    <Card title="Berufe" icon={icon}>
      {/* Kennzahlen */}
      <div className="mb-5 grid grid-cols-3 gap-4">
        <Stat label="Personen mit Beruf" value={o.personsWithOccupation} />
        <Stat label="Verschiedene Berufe" value={o.distinctTitles} />
        <Stat label="Stationen gesamt" value={o.totalStations} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Häufigste Berufe */}
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Häufigste Berufe</h4>
          <div className="space-y-1.5">
            {o.topOccupations.map((t) => (
              <Bar key={t.title} label={t.title} value={t.count} max={maxTop} color="bg-teal-500" />
            ))}
          </div>
        </div>

        {/* Berufe nach Geschlecht */}
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Berufe nach Geschlecht</h4>
          <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {GENDER_SERIES.map((serie) => (
              <span key={serie.key} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: serie.color }} />
                {serie.label}
              </span>
            ))}
          </div>
          <div className="space-y-1.5">
            {o.byGender.map((row) => (
              <GenderStackRow key={row.title} row={row} maxTotal={maxGenderTotal} />
            ))}
          </div>
        </div>
      </div>

      {/* Berufe im Wandel */}
      <div className="mt-6">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Berufe im Wandel</h4>
        {o.byPeriod.length === 0 ? (
          <p className="text-xs text-slate-400">Für diese Auswertung brauchen Stationen ein Von-Jahr.</p>
        ) : (
          <div className="space-y-3">
            {o.byPeriod.map((p) => {
              const top3 = p.topTitles.slice(0, 3);
              const maxPeriod = Math.max(...top3.map((t) => t.count), 1);
              return (
                <div key={p.period} className="flex items-start gap-3 text-xs">
                  <span className="w-20 shrink-0 pt-0.5 text-right font-mono text-slate-500">{p.period}</span>
                  <div className="flex-1 space-y-1">
                    {top3.map((t) => (
                      <div key={t.title} className="flex items-center gap-2">
                        <span className="w-24 shrink-0 truncate text-slate-600 sm:w-32" title={t.title}>{t.title}</span>
                        <div className="h-2.5 flex-1 rounded-full bg-slate-100">
                          <div
                            className="h-2.5 rounded-full bg-teal-400"
                            style={{ width: `${Math.max((t.count / maxPeriod) * 100, 3)}%` }}
                          />
                        </div>
                        <span className="w-6 shrink-0 text-right font-medium text-slate-700">{t.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

function TimelineSection({ s }: { s: Statistics }) {
  const allDecades = [...new Set([...s.birthsByDecade.map((d) => d.decade), ...s.deathsByDecade.map((d) => d.decade)])].sort();
  const birthMap = new Map(s.birthsByDecade.map((d) => [d.decade, d.count]));
  const deathMap = new Map(s.deathsByDecade.map((d) => [d.decade, d.count]));
  const maxVal = Math.max(
    ...s.birthsByDecade.map((d) => d.count),
    ...s.deathsByDecade.map((d) => d.count),
    1,
  );

  return (
    <Card title="Zeitstrahl (Geburten & Tode pro Jahrzehnt)">
      {allDecades.length === 0 ? (
        <p className="text-sm text-slate-400">Keine Datumsdaten vorhanden.</p>
      ) : (
        <div className="space-y-1">
          {allDecades.map((decade) => {
            const b = birthMap.get(decade) ?? 0;
            const d = deathMap.get(decade) ?? 0;
            return (
              <div key={decade} className="flex items-center gap-2 text-xs">
                <span className="w-14 text-right font-mono text-slate-500">{decade}</span>
                <div className="flex flex-1 gap-0.5">
                  <div className="flex-1 rounded-full bg-slate-50 h-3">
                    <div className="h-3 rounded-full bg-green-400" style={{ width: `${(b / maxVal) * 100}%` }} />
                  </div>
                  <div className="flex-1 rounded-full bg-slate-50 h-3">
                    <div className="h-3 rounded-full bg-red-400" style={{ width: `${(d / maxVal) * 100}%` }} />
                  </div>
                </div>
                <span className="w-12 text-right">
                  <span className="text-green-600">{b}</span>/<span className="text-red-500">{d}</span>
                </span>
              </div>
            );
          })}
          <div className="flex justify-center gap-4 pt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-400" /> Geburten</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400" /> Tode</span>
          </div>
        </div>
      )}
    </Card>
  );
}

function ChildrenSection({ s }: { s: Statistics }) {
  const max = Math.max(...s.childrenPerFamily.distribution.map((d) => d.families), 1);
  return (
    <Card title="Kinder pro Familie">
      <div className="mb-3 flex justify-around">
        <Stat label="Durchschnitt" value={s.childrenPerFamily.average} />
        {s.childrenPerFamily.max && (
          <Stat label="Maximum" value={s.childrenPerFamily.max.count} />
        )}
      </div>
      {s.childrenPerFamily.max && (
        <p className="mb-3 text-center text-xs text-slate-400">
          Größte Familie: {s.childrenPerFamily.max.parents} ({s.childrenPerFamily.max.count} Kinder)
        </p>
      )}
      <div className="space-y-1.5">
        {s.childrenPerFamily.distribution.map((d) => (
          <Bar key={d.children} label={`${d.children} Kind${d.children !== 1 ? 'er' : ''}`} value={d.families} max={max} color="bg-amber-500" />
        ))}
      </div>
    </Card>
  );
}

function MarriageAgeSection({ s }: { s: Statistics }) {
  if (!s.averageMarriageAge && s.marriageAgeByDecade.length === 0) {
    return (
      <Card title="Heiratsalter">
        <p className="text-sm text-slate-400">Keine Heiratsdaten mit Geburtsdaten vorhanden.</p>
      </Card>
    );
  }
  const max = Math.max(...s.marriageAgeByDecade.map((d) => d.avgAge), 1);
  return (
    <Card title="Heiratsalter">
      {s.averageMarriageAge && (
        <div className="mb-3 text-center">
          <Stat label="Durchschnittliches Heiratsalter" value={`${s.averageMarriageAge} Jahre`} />
        </div>
      )}
      {s.marriageAgeByDecade.length > 0 && (
        <div className="space-y-1.5">
          {s.marriageAgeByDecade.map((d) => (
            <Bar key={d.decade} label={d.decade} value={d.avgAge} max={max} color="bg-purple-500" />
          ))}
        </div>
      )}
    </Card>
  );
}

function MigrationSection({ s }: { s: Statistics }) {
  if (s.migrations.length === 0) {
    return (
      <Card title="Migrationen (Geburtsort → Sterbeort)">
        <p className="text-sm text-slate-400">Keine Migrationsdaten vorhanden.</p>
      </Card>
    );
  }
  return (
    <Card title="Migrationen (Geburtsort → Sterbeort)">
      <div className="space-y-2">
        {s.migrations.map((m, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="flex-1 truncate text-right text-slate-600" title={m.from}>{m.from}</span>
            <svg className="h-4 w-4 flex-shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
            <span className="flex-1 truncate text-slate-600" title={m.to}>{m.to}</span>
            <span className="w-6 text-right font-medium text-slate-700">{m.count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NameCloudSection({ s }: { s: Statistics }) {
  const maxCount = Math.max(...s.topFirstNames.map((n) => n.count), 1);

  // Font size: min 11px → max 32px, scaled logarithmically
  function fontSize(count: number): number {
    const ratio = Math.log(count + 1) / Math.log(maxCount + 1);
    return Math.round(11 + ratio * 21);
  }

  // Colour palette: cycle through a set of muted tones
  const PALETTE = [
    '#3b82f6','#ec4899','#8b5cf6','#10b981','#f59e0b',
    '#06b6d4','#f97316','#6366f1','#14b8a6','#ef4444',
  ];

  if (s.topFirstNames.length === 0) {
    return (
      <Card title="Vornamen-Wolke">
        <p className="text-sm text-slate-400">Keine Vornamen vorhanden.</p>
      </Card>
    );
  }

  return (
    <Card title="Vornamen-Wolke">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-2 leading-snug">
        {s.topFirstNames.map((n, i) => (
          <span
            key={n.name}
            title={`${n.name}: ${n.count}×`}
            style={{
              fontSize: fontSize(n.count),
              color: PALETTE[i % PALETTE.length],
              fontWeight: n.count === maxCount ? 700 : n.count > maxCount * 0.5 ? 600 : 400,
              opacity: 0.75 + (n.count / maxCount) * 0.25,
            }}
          >
            {n.name}
          </span>
        ))}
      </div>
    </Card>
  );
}

function LifeExpectancySection({ s }: { s: Statistics }) {
  return (
    <Card title="Lebenserwartung im Wandel">
      {s.lifeExpectancyByDecade.length === 0 ? (
        <p className="text-sm text-slate-400">
          Keine Personen mit Geburts- und Sterbedatum vorhanden.
        </p>
      ) : (
        <>
          <p className="mb-2 text-xs text-slate-400">
            Durchschnittsalter bei Tod nach Geburtsjahrzehnt.
          </p>
          <LineChart
            data={s.lifeExpectancyByDecade.map((d) => ({ label: d.decade, value: d.avgAge, count: d.count }))}
            unit="J."
            color="#6366f1"
          />
        </>
      )}
    </Card>
  );
}

function FunFactsSection({ s }: { s: Statistics }) {
  if (s.funFacts.length === 0) {
    return null;
  }
  return (
    <Card title="Fun Facts">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {s.funFacts.map((fact, i) => (
          <div
            key={i}
            className="flex flex-col items-center rounded-lg border border-slate-100 bg-slate-50 p-3 text-center"
          >
            <span className="mb-1 text-2xl">{fact.icon}</span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {fact.title}
            </span>
            <span className="mt-0.5 text-base font-bold text-slate-800">{fact.value}</span>
            {fact.detail && (
              <span className="mt-0.5 text-xs text-slate-500 leading-tight">{fact.detail}</span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── StatsPage ──────────────────────────────────────────────────────────────

export function StatsPage() {
  const { data: stats, isLoading, isError } = useStatistics();

  return (
    <AppLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-5xl">
          <h1 className="mb-6 text-2xl font-bold text-slate-800">Statistiken</h1>

          {isLoading && (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" message="Statistiken werden berechnet..." />
            </div>
          )}

          {isError && (
            <p className="text-center text-red-500">Fehler beim Laden der Statistiken.</p>
          )}

          {stats && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Übersicht: volle Breite */}
              <div className="sm:col-span-2">
                <OverviewSection s={stats} />
              </div>

              <div className="sm:col-span-2"><FunFactsSection s={stats} /></div>

              <GenderSection s={stats} />
              <AgeSection s={stats} />

              {/* Lebenserwartung im Wandel: volle Breite */}
              <div className="sm:col-span-2"><LifeExpectancySection s={stats} /></div>

              <PlacesSection s={stats} />

              <div className="sm:col-span-2"><NameCloudSection s={stats} /></div>

              {/* Berufe: volle Breite */}
              <div className="sm:col-span-2"><OccupationSection s={stats} /></div>

              <NamesSection s={stats} />
              <TimelineSection s={stats} />

              <ChildrenSection s={stats} />
              <MarriageAgeSection s={stats} />

              {/* Migrationen: volle Breite */}
              <div className="sm:col-span-2"><MigrationSection s={stats} /></div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
